import {
  useCallback,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import { useAuth } from '../auth/AuthProvider';
import { isGuestUser } from '../auth/guest';
import {
  LegalReacceptGate,
  type LegalReacceptGateState,
} from '../auth/LegalReacceptGate';
import { setUsageBlocked } from '../auth/legalGateSignal';
import { Button, Modal } from '../components/ui';
import { useT } from '../i18n';
import {
  isProactivePushEnabled,
  isStartupPushPromptDone,
} from '../native/pushPreferences';
import {
  onPushPrimingRequest,
  type PushPrimingReason,
} from '../native/pushPrimingBus';
import { CompleteProfileGate } from './CompleteProfileGate';
import { StartupGateContext, useStartupGate, type StartupGate } from './startupGateContext';

// W2-B/N-05: Kontext + Hook leben in startupGateContext.ts (die Tour liest ihn
// jetzt auch); hier re-exportiert, damit bestehende Importe gueltig bleiben.
export { useStartupGate };
export type { StartupGate };

/**
 * W7-B (E12): Grund → i18n-Key fuer den Dialogtext. 'fallback' bekommt bewusst
 * KEINEN eigenen Key — dort bleibt die allgemeine Kopie `startup.push.body`
 * stehen (Karte C §Empfehlung d, Punkt 2).
 */
const PUSH_PRIMING_REASON_KEYS: Record<PushPrimingReason, string | null> = {
  companion_wants_to_write: 'push_priming.reason.companion',
  adventure_ready: 'push_priming.reason.adventure',
  adventure_planning: 'push_priming.reason.adventure_planning',
  settings: null,
  fallback: null,
};

// W7-B: Welches Startup-Gate steht gerade? Der Cross-Onboarding-Dialog und seit
// W2-B/N-05 auch die Tour haengen daran — sie duerfen sich NIE ueber ein Gate
// stapeln (Karte C §Empfehlung e, letzter Punkt). Default 'none'.

interface StartupGateState {
  legalState: LegalReacceptGateState;
  profileIncomplete: boolean;
  /** W1-B: Gastkonto — hat bewusst kein Geburtsdatum. Default false. */
  isGuest?: boolean;
  pushHintPending: boolean;
  nativePromptPending: boolean;
}

export function selectStartupGate({
  legalState,
  profileIncomplete,
  isGuest = false,
  pushHintPending,
  nativePromptPending,
}: StartupGateState): StartupGate {
  if (legalState !== 'clear') return 'legal';
  // W1-B: Gaeste haben kein Geburtsdatum und duerfen deshalb NICHT in das
  // nicht schliessbare Profil-Gate laufen — sonst sperrt die erste Tuer jeden
  // Gast sofort aus. Das Gate bleibt fuer OAuth-Neukonten und Altbestand.
  if (profileIncomplete && !isGuest) return 'complete-profile';
  if (pushHintPending) return 'push-hint';
  if (nativePromptPending) return 'native-push';
  return 'none';
}

export function StartupGateCoordinator({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const authKey = auth.status === 'authed' && auth.user
    ? String(auth.user.userId)
    : null;
  const [legalReport, setLegalReport] = useState<{
    authKey: string;
    state: LegalReacceptGateState;
  } | null>(null);
  const [nativePromptPending, setNativePromptPending] = useState(false);
  // W7-B (E12): der EREIGNIS-Auslöser. null = kein Priming angefragt; sonst der
  // Grund, der im Dialog steht. Der Merker `startupPushPermissionPending` aus
  // dem AuthProvider bleibt als Netz fuer wiederkehrende Nutzer bestehen.
  const [primingReason, setPrimingReason] = useState<PushPrimingReason | null>(null);

  const handleLegalStateChange = useCallback((state: LegalReacceptGateState) => {
    if (!authKey) return;
    setLegalReport({ authKey, state });
  }, [authKey]);

  // Ein neuer Auth-Nutzer gilt bis zum Ergebnis von /legal-status als ungeklärt.
  // Dadurch kann das Profil-Gate nicht fuer einen Render-Zyklus vorspringen.
  const legalState: LegalReacceptGateState = authKey
    && legalReport?.authKey === authKey
    ? legalReport.state
    : authKey
      ? 'checking'
      : 'clear';
  const profileIncomplete = auth.status === 'authed'
    && auth.user?.profile_incomplete === true;
  const pushHintPending = auth.status === 'authed'
    && (auth.startupPushPermissionPending || primingReason !== null);
  const activeGate = selectStartupGate({
    legalState,
    profileIncomplete,
    isGuest: isGuestUser(auth.user),
    pushHintPending,
    nativePromptPending: auth.status === 'authed' && nativePromptPending,
  });

  // Solange das Rechtstexte-Gate nicht 'clear' ist, sperrt der Server die
  // Nutzung (409 legal_acceptance_required). Der Content-Bootstrap im
  // SyncProvider wartet darauf — sonst liefe er in lauter 409er, und sein
  // Vollbild verdeckte den Zustimmungsdialog (docs/20-gotchas.md, 19.10.2026).
  // 'checking' zaehlt mit: ein Konto mit offener Zustimmung bekaeme schon die
  // ersten Bootstrap-Anfragen abgewiesen. Effekte laufen Kind-vor-Eltern, das
  // Signal steht also, bevor der SyncProvider seine Engine startet.
  const usageBlocked = legalState !== 'clear';
  useEffect(() => {
    setUsageBlocked(authKey, usageBlocked);
  }, [authKey, usageBlocked]);
  useEffect(() => () => setUsageBlocked(null, false), []);

  // W7-B: Ereignis-Anfragen entgegennehmen. Die harten Grenzen (Karte C §d
  // Punkt 4) liegen bewusst HIER, beim einzigen Empfaenger — so kann kein
  // Ausloeser sie vergessen:
  //   • isStartupPushPromptDone() → die Frage wurde schon beantwortet
  //   • isProactivePushEnabled()  → der Nutzer hat den Dialog abgeschaltet
  // Der Done-Merker wird NICHT hier gesetzt, sondern beim Beantworten (und dort
  // VOR dem OS-Aufruf, AuthProvider.requestStartupPushPermission).
  useEffect(() => onPushPrimingRequest((reason) => {
    // Ein ausdruecklicher Wunsch aus Einstellungen darf erneut fragen,
    // benutzt aber exakt denselben Hinweis und dieselben Startup-Gates.
    if (reason !== 'settings' && isStartupPushPromptDone()) return;
    if (reason !== 'settings' && !isProactivePushEnabled()) return;
    setPrimingReason((current) => current ?? reason);
  }), []);

  // Meldet sich der Nutzer ab, darf kein Grund aus der alten Sitzung stehen
  // bleiben (sonst zeigt der naechste Login den Dialog ohne Anlass).
  useEffect(() => {
    if (auth.status !== 'authed') {
      setPrimingReason(null);
      setNativePromptPending(false);
    }
  }, [auth.status]);

  const handlePushDismiss = useCallback(() => {
    // markStartupPushPromptDone() steckt in dismissStartupPushPermission — auch
    // ein Priming-„Nicht jetzt" ist damit endgueltig.
    auth.dismissStartupPushPermission();
    setPrimingReason(null);
  }, [auth]);

  const handlePushAccept = useCallback(() => {
    // Zuerst den App-Dialog aus dem Baum nehmen. Der Effect unten startet den
    // nativen Prompt erst nach diesem Commit und nur, wenn kein Gate nachrueckt.
    auth.dismissStartupPushPermission();
    setPrimingReason(null);
    setNativePromptPending(true);
  }, [auth]);

  useEffect(() => {
    if (activeGate !== 'native-push') return;
    setNativePromptPending(false);
    // Der Rueckgabewert wird bewusst VERWORFEN: auf Web/PWA liefert
    // requestPushPermission() IMMER false (bridge.ts) — das ist keine
    // Ablehnung, sondern nur „der echte Zustand kommt asynchron ueber das
    // Web-SDK". Ein false darf hier also nichts ausloesen (Karte C §d Punkt 5).
    void auth.requestStartupPushPermission().catch(() => undefined);
  }, [activeGate, auth]);

  return (
    <>
      <LegalReacceptGate
        key={authKey ?? 'anonymous'}
        onGateStateChange={handleLegalStateChange}
      >
        <StartupGateContext.Provider value={activeGate}>
          {children}
        </StartupGateContext.Provider>
      </LegalReacceptGate>
      {activeGate === 'complete-profile' ? <CompleteProfileGate /> : null}
      {activeGate === 'push-hint' ? (
        <StartupPushPermissionGate
          reasonKey={primingReason
            ? PUSH_PRIMING_REASON_KEYS[primingReason] ?? undefined
            : undefined}
          onAccept={handlePushAccept}
          onDismiss={handlePushDismiss}
        />
      ) : null}
    </>
  );
}

interface StartupPushPermissionGateProps {
  /**
   * W7-B: i18n-Key des GRUNDES ("Dein Begleiter moechte dir schreiben"). Fehlt
   * er, bleibt die allgemeine Kopie `startup.push.body` stehen.
   */
  reasonKey?: string;
  onAccept: () => void;
  onDismiss: () => void;
}

export function StartupPushPermissionGate({
  reasonKey,
  onAccept,
  onDismiss,
}: StartupPushPermissionGateProps) {
  const { t } = useT();

  return (
    <Modal
      open
      onClose={onDismiss}
      closable={false}
      title={t('startup.push.title')}
      maxWidth={480}
      footer={(
        <>
          <Button variant="secondary" onClick={onDismiss}>
            {t('startup.push.later')}
          </Button>
          <Button data-autofocus onClick={onAccept}>
            {t('startup.push.allow')}
          </Button>
        </>
      )}
    >
      <p>{t(reasonKey ?? 'startup.push.body')}</p>
    </Modal>
  );
}
